import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import {
  activateCompany,
  deactivateCompany,
  deleteCompany,
  getCompany,
  getEconomyStatus,
  listCompanies,
} from "@/lib/economy-api";
import { formatGros } from "@/lib/currency";
import { ASSETS } from "@/assets";
import { CompanyFormDialog } from "@/components/economy/CompanyFormDialog";
import { ShareManagerDialog } from "@/components/economy/ShareManagerDialog";
import { MarketAdjustDialog } from "@/components/economy/MarketAdjustDialog";
import { AccountShell } from "@/components/account/AccountShell";
import { ECONOMY_ADMIN } from "@/constants/testIds";

export const EconomyAdminPage = () => {
  const { user, loading } = useAuth();
  const [companies, setCompanies] = useState(null);
  const [status, setStatus] = useState(null);
  const [formCompany, setFormCompany] = useState(undefined); // undefined=closed, null=create, object=edit
  const [sharesCompany, setSharesCompany] = useState(null);
  const [marketCompany, setMarketCompany] = useState(null);

  const refresh = useCallback(async () => {
    const [list, econStatus] = await Promise.all([listCompanies(true), getEconomyStatus()]);
    setCompanies(list);
    setStatus(econStatus);
  }, []);

  useEffect(() => {
    if (user?.is_gm) refresh().catch(() => toast("Nepodařilo se načíst podniky"));
  }, [user, refresh]);

  const openShares = async (companyId) => {
    try {
      setSharesCompany(await getCompany(companyId));
    } catch {
      toast("Nepodařilo se načíst detail podniku");
    }
  };

  const refreshShares = async () => {
    if (sharesCompany) setSharesCompany(await getCompany(sharesCompany.id));
    refresh();
  };

  const handleToggleActive = async (company) => {
    try {
      if (company.is_active) await deactivateCompany(company.id);
      else await activateCompany(company.id);
      refresh();
    } catch {
      toast("Akce se nezdařila");
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Opravdu smazat podnik „${company.name}“? Historie zůstane zachována.`)) return;
    try {
      await deleteCompany(company.id);
      toast("Podnik byl smazán");
      refresh();
    } catch {
      toast("Smazání se nezdařilo");
    }
  };

  if (loading) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40 text-center">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white/40">Načítám…</p>
      </section>
    );
  }

  if (!user) return <Navigate to="/ucet" replace />;

  if (!user.is_gm) {
    return (
      <AccountShell isGm={false} testId={ECONOMY_ADMIN.guard}>
        <div className="mx-auto max-w-lg rounded-xl border border-white/10 bg-[#17171b] p-8 text-center">
          <p className="text-sm text-white/60">
            Tahle sekce Horowitzovy banky je jen pro GM. Účetní knihy nejsou pro veřejnost.
          </p>
          <Link to="/ucet" className="mt-4 inline-block text-sm text-[#F0801F] hover:underline">
            Zpět na účet
          </Link>
        </div>
      </AccountShell>
    );
  }

  return (
    <AccountShell isGm={user.is_gm} testId={ECONOMY_ADMIN.page}>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img
            src={ASSETS.logoBanka}
            alt="Horowitzova městská banka"
            className="h-14 w-14 shrink-0 rounded-full border border-white/10 bg-[#17171b] object-contain p-1.5"
          />
          <div>
            <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Horowitzova městská banka - správa
            </p>
            <h1 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
              Ekonomika
            </h1>
          </div>
        </div>
        <button
          onClick={() => setFormCompany(null)}
          data-testid={ECONOMY_ADMIN.createCompanyButton}
          className="rounded-md bg-[#F0801F] px-5 py-3 font-display text-xs font-600 uppercase tracking-[0.14em] text-[#141417] hover:bg-[#ff9438]"
        >
          + Nový podnik
        </button>
      </div>

      {status && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div
            className="rounded-xl border border-white/10 bg-[#17171b] p-6"
            data-testid={ECONOMY_ADMIN.payoutStatusCard}
          >
            <div className="mb-4 flex items-center gap-2 text-[#F0801F]">
              <i className="fa-solid fa-sack-dollar text-sm" />
              <p className="font-display text-xs font-600 uppercase tracking-[0.2em]">Výplaty</p>
            </div>
            {status.last_run ? (
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-white/40">Poslední běh</dt>
                  <dd className="text-white/80">
                    {new Date(status.last_run.scheduled_for).toLocaleString("cs-CZ")}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-white/40">Stav</dt>
                  <dd>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[11px] uppercase tracking-wide ${
                        status.last_run.status === "completed"
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-white/10 text-white/60"
                      }`}
                    >
                      {status.last_run.status === "completed" ? "Dokončeno" : status.last_run.status}
                    </span>
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-white/40">Zpracováno podniků</dt>
                  <dd className="text-white/80">{status.last_run.companies_processed}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-white/40">Zatím žádný běh.</p>
            )}
            {status.next_scheduled_for && (
              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3 text-sm">
                <span className="text-white/40">Další plánovaný běh</span>
                <span className="text-[#F0801F]">
                  {new Date(status.next_scheduled_for).toLocaleString("cs-CZ")}
                </span>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-white/10 bg-[#17171b] p-6">
            <div className="mb-4 flex items-center gap-2 text-[#F0801F]">
              <i className="fa-solid fa-chart-line text-sm" />
              <p className="font-display text-xs font-600 uppercase tracking-[0.2em]">Trh</p>
            </div>
            {status.last_market_run ? (
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-white/40">Poslední pohyb</dt>
                  <dd className="text-white/80">
                    {new Date(status.last_market_run.recorded_at).toLocaleString("cs-CZ")}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-white/40">Zpracováno podniků</dt>
                  <dd className="text-white/80">{status.last_market_run.companies_processed}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-white/40">Zatím žádný pohyb.</p>
            )}
            {status.next_market_scheduled_for && (
              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3 text-sm">
                <span className="text-white/40">Další plánovaný běh</span>
                <span className="text-[#F0801F]">
                  {new Date(status.next_market_scheduled_for).toLocaleString("cs-CZ")}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {companies === null && <p className="text-sm text-white/40">Načítám podniky…</p>}
        {companies?.length === 0 && <p className="text-sm text-white/50">Zatím žádné podniky.</p>}
        {companies?.map((c) => (
          <div
            key={c.id}
            data-testid={ECONOMY_ADMIN.companyRow}
            className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#17171b] p-6"
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-base font-600 text-white">{c.name}</span>
                {c.deleted && (
                  <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase text-white/40">
                    Smazáno
                  </span>
                )}
                {!c.deleted && !c.is_active && (
                  <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase text-white/40">
                    Neaktivní
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-white/50">
                Hodnota {formatGros(c.total_value_gros)} · Zisk/týden {formatGros(c.weekly_profit_base_gros)} ·{" "}
                {c.shares_assigned} % přiděleno ({c.shareholder_count} podílníků)
              </p>
              <p className="mt-1 text-xs text-white/40">
                NPC vlastník: {c.npc_owner_name} · K prodeji hráčům {c.percent_for_sale} % (volných{" "}
                {c.available_for_sale} %)
              </p>
            </div>
            {!c.deleted && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFormCompany(c)}
                  data-testid={ECONOMY_ADMIN.editCompanyButton}
                  className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F]"
                >
                  Upravit
                </button>
                <button
                  onClick={() => openShares(c.id)}
                  data-testid={ECONOMY_ADMIN.manageSharesButton}
                  className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F]"
                >
                  Podíly
                </button>
                <button
                  onClick={() => setMarketCompany(c)}
                  className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F]"
                >
                  Trh
                </button>
                <button
                  onClick={() => handleToggleActive(c)}
                  data-testid={ECONOMY_ADMIN.toggleActiveButton}
                  className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F]"
                >
                  {c.is_active ? "Deaktivovat" : "Aktivovat"}
                </button>
                <button
                  onClick={() => handleDelete(c)}
                  data-testid={ECONOMY_ADMIN.deleteCompanyButton}
                  className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-red-400/60 hover:text-red-300"
                >
                  Smazat
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {formCompany !== undefined && (
        <CompanyFormDialog
          open
          company={formCompany}
          onClose={() => setFormCompany(undefined)}
          onSaved={() => {
            setFormCompany(undefined);
            refresh();
          }}
        />
      )}

      {sharesCompany && (
        <ShareManagerDialog
          open
          company={sharesCompany}
          onClose={() => setSharesCompany(null)}
          onChanged={refreshShares}
        />
      )}

      {marketCompany && (
        <MarketAdjustDialog
          open
          company={marketCompany}
          onClose={() => setMarketCompany(null)}
          onAdjusted={() => {
            setMarketCompany(null);
            refresh();
          }}
        />
      )}
    </AccountShell>
  );
};
